import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';
import { getIntersectionRoot, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

const STRIPE_DURATION_OPEN = 1;
const STRIPE_DURATION_CLOSE = 0.8;
const BOTTLE_DURATION_OPEN = 0.75;
const BOTTLE_DURATION_CLOSE = 0.95;
const BUTTON_DURATION_OPEN = 0.28;
const BUTTON_DURATION_CLOSE = 0.8;
const BOTTLE_DELAY_CLOSE = 380;
const BUTTON_DELAY_CLOSE = 1020;
const REVEAL_BUTTON_DELAY = BUTTON_DELAY_CLOSE - BOTTLE_DELAY_CLOSE;
const STAGGER_OPEN = 120;
const STAGGER_CLOSE = 90;
const STAGGER_OPEN_SPAN = STAGGER_OPEN * 2;
const STAGGER_CLOSE_SPAN = STAGGER_CLOSE * 2;
const BLOOM_DURATION_OPEN = 1.05;
const BLOOM_DURATION_CLOSE = 0.48;
const BLOOM_BUTTON_DURATION_CLOSE = 0.42;
const REVEAL_DURATION = 0.95;
const HOVER_QUERY = '(hover: hover) and (pointer: fine)';
const BUTTON_HOVER_SCALE = 1.08;
const BUTTON_PRESS_SCALE = 0.96;
const BOTTLE_HOVER_SCALE = 1.03;
const BOTTLE_PRESS_SCALE = 0.975;
const BUTTON_MAGNET_PULL = 0.18;
const BUTTON_MAGNET_MAX = 11;
const BOTTLE_MAGNET_PULL = 0.12;
const BOTTLE_MAGNET_MAX = 8;
const FILL_EASE = 'cubic-bezier(0.33, 0.12, 0.2, 1)';
const FILL_MS_OPEN = 420;
const FILL_MS_CLOSE = 320;
const FILL_RADIUS = 120;
const BOTTLE_SCALE_HIDDEN = 0.92;
const BLOOM_BOTTLE_SCALE = 0.84;
const BLOOM_BOTTLE_SINK = 22;
const BOTTLE_BLUR_MAX = 8;
const STRIPE_BLUR_MAX = 4;
const STRIPE_SCALE = 1.08;
const STRIPE_SETTLED = 0.98;
const STRIPE_COPY_REVEAL = 0.6;
const BLOOM_ORIGIN_Y = 0.3;
const BLOOM_FEATHER = 160;
const IDLE_HINT_FIRST = 0.22;
const IDLE_HINT_REPEAT = 20;
const NUDGE_LIFT = -5;
const NUDGE_ROTATE = 1.35;
const NUDGE_SCALE = 1.012;
const REST_DELTA = 0.001;
const REST_VELOCITY = 0.01;
const AWAY_COVER = 0.82;

/**
 * @param {number} duration
 * @param {number} [bounce]
 */
function springFromDuration(duration, bounce = 0) {
  const dampingRatio = 1 - bounce;
  const stiffness = (2 * Math.PI / duration) ** 2;
  const damping = 2 * dampingRatio * Math.sqrt(stiffness);
  return { stiffness, damping };
}

const BUTTON_MAGNET_SPRING = springFromDuration(0.62, 0.1);
const BUTTON_SCALE_SPRING = springFromDuration(0.44, 0.06);
const BOTTLE_MAGNET_SPRING = springFromDuration(0.62, 0);
const BOTTLE_SCALE_SPRING = springFromDuration(0.48, 0);
const NUDGE_OUT_SPRING = springFromDuration(0.56, 0);
const NUDGE_BACK_SPRING = springFromDuration(0.78, 0);

/**
 * @param {number} value
 * @param {number} min
 * @param {number} max
 */
function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

/**
 * @param {number} value
 * @param {number} start
 * @param {number} end
 */
function rangeProgress(value, start, end) {
  if (end <= start) return value >= end ? 1 : 0;
  return clamp((value - start) / (end - start), 0, 1);
}

/**
 * @param {number} value
 */
function smoothstep(value) {
  const t = clamp(value, 0, 1);
  return t * t * (3 - 2 * t);
}

/** @param {number} value */
function px(value) {
  return Math.round(value * 100) / 100;
}

/** @param {number} value */
function milli(value) {
  return Math.round(value * 1000) / 1000;
}

/** @param {number} value */
function alpha(value) {
  return String(milli(clamp(value, 0, 1)));
}

/**
 * Hidden layers skip the blur. Shown blurs quantize to 0.1px so the
 * long spring tail does not rebuild a filter every frame.
 *
 * @param {number} amount
 * @param {boolean} shown
 */
function blurFilter(amount, shown) {
  if (!shown) return '';
  const stepped = Math.round(amount * 10) / 10;
  if (stepped < 0.1) return '';
  return `blur(${stepped}px)`;
}

/**
 * @param {SpringValue} spring
 * @param {number} dt
 */
function stepSpring(spring, dt) {
  if (spring.settled) return false;
  spring.step(dt);
  return true;
}

class StyleWrites {
  /** @type {Map<HTMLElement, Map<string, string>>} */
  #current = new Map();

  /**
   * @param {HTMLElement | undefined} node
   * @param {string} property
   * @param {string} value
   */
  set(node, property, value) {
    if (!node) return;

    let current = this.#current.get(node);
    if (!current) {
      current = new Map();
      this.#current.set(node, current);
    }
    if (current.get(property) === value) return;

    current.set(property, value);
    node.style[property] = value;
  }

  forget() {
    this.#current.clear();
  }
}

class SpringValue {
  value = 0;
  velocity = 0;
  target = 0;
  stiffness = 80;
  damping = 18;

  /**
   * @param {number} value
   */
  constructor(value = 0) {
    this.value = value;
    this.target = value;
  }

  /**
   * @param {{ stiffness: number, damping: number }} config
   */
  setConfig(config) {
    this.stiffness = config.stiffness;
    this.damping = config.damping;
  }

  /**
   * @returns {boolean}
   */
  get settled() {
    return Math.abs(this.value - this.target) < REST_DELTA && Math.abs(this.velocity) < REST_VELOCITY;
  }

  /**
   * @param {number} dt
   */
  step(dt) {
    const t = Math.min(dt, 0.064);
    const acceleration = (-this.stiffness * (this.value - this.target) - this.damping * this.velocity);
    this.velocity += acceleration * t;
    this.value += this.velocity * t;

    if (this.settled) {
      this.value = this.target;
      this.velocity = 0;
    }
  }
}

/**
 * @typedef {object} Refs
 * @property {HTMLElement} idle
 * @property {HTMLElement} panel
 * @property {HTMLElement} trigger
 * @property {HTMLElement} visual
 * @property {HTMLElement} nudge
 * @property {HTMLElement} [reveal]
 * @property {HTMLElement} [caption]
 * @property {HTMLElement} [bottle]
 * @property {HTMLElement | HTMLElement[]} [stripes]
 *
 * @extends {Component<Refs>}
 */
export class SkreySmellThis extends Component {
  requiredRefs = ['idle', 'panel', 'trigger', 'visual', 'nudge'];

  /** @type {boolean} */
  #isOpen = false;

  /** @type {number | null} */
  #rafId = null;

  /** @type {number} */
  #lastTime = 0;

  /** @type {SpringValue} */
  #bottleSpring = new SpringValue(0);

  /** @type {SpringValue} */
  #buttonSpring = new SpringValue(0);

  /** @type {SpringValue} */
  #bloomSpring = new SpringValue(0);

  /** @type {SpringValue} */
  #revealSpring = new SpringValue(1);

  /** @type {SpringValue} */
  #magnetX = new SpringValue(0);

  /** @type {SpringValue} */
  #magnetY = new SpringValue(0);

  /** @type {SpringValue} */
  #scaleSpring = new SpringValue(1);

  /** @type {SpringValue} */
  #nudgeY = new SpringValue(0);

  /** @type {SpringValue} */
  #nudgeRotate = new SpringValue(0);

  /** @type {SpringValue} */
  #nudgeScale = new SpringValue(1);

  /** @type {'rest' | 'out' | 'back'} */
  #nudgePhase = 'rest';

  /** @type {number} */
  #nudgeSign = 1;

  /** @type {boolean} */
  #pendingFirstHint = false;

  /** @type {boolean} */
  #hasOpened = false;

  /** @type {number | null} */
  #idleHintTimer = null;

  /** @type {boolean} */
  #hovering = false;

  /** @type {boolean} */
  #pressed = false;

  /** @type {{ left: number, top: number, width: number, height: number, cx: number, cy: number } | null} */
  #restRect = null;

  /**
   * Untransformed border box.
   * Magnet math reuses it instead of reading layout on pointermove.
   *
   * @type {{ left: number, top: number, width: number, height: number } | null}
   */
  #layoutRect = null;

  /** @type {number} */
  #pointerX = 0;

  /** @type {number} */
  #pointerY = 0;

  /** @type {boolean} */
  #pointerPending = false;

  /** @type {boolean} */
  #layoutDirty = false;

  /** @type {HTMLElement[]} */
  #stripeNodes = [];

  /** @type {HTMLElement[]} */
  #stripeMedias = [];

  /** @type {boolean} */
  #stripesParked = false;

  /** @type {StyleWrites} */
  #styles = new StyleWrites();

  /** @type {MediaQueryList} */
  #hoverMedia = window.matchMedia(HOVER_QUERY);

  /** @type {number | null} */
  #scrollRaf = null;

  /** @type {number | null} */
  #measureRaf = null;

  /** @type {boolean} */
  #needsMeasure = false;

  /** @type {EventTarget | null} */
  #hoverScrollTarget = null;

  /** @type {SpringValue[]} */
  #stripeSprings = [];

  /** @type {number} */
  #originX = 0;

  /** @type {number} */
  #originY = 0;

  /** @type {number} */
  #coverRadius = 1;

  /** @type {boolean} */
  #hasRevealed = false;

  /** @type {IntersectionObserver | null} */
  #revealObserver = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {EventTarget | null} */
  #scrollTarget = null;

  /** @type {EventTarget | null} */
  #awayScrollTarget = null;

  /** @type {number[]} */
  #timeouts = [];

  /** @type {MediaQueryList | null} */
  #reducedMotionQuery = null;

  get #isBloom() {
    return this.dataset.open === 'bloom';
  }

  get #isCaption() {
    return this.dataset.trigger === 'caption';
  }

  get #nudgeEnabled() {
    return this.hasAttribute('data-nudge');
  }

  get #hoverScale() {
    return this.#isCaption ? BOTTLE_HOVER_SCALE : BUTTON_HOVER_SCALE;
  }

  get #pressScale() {
    return this.#isCaption ? BOTTLE_PRESS_SCALE : BUTTON_PRESS_SCALE;
  }

  connectedCallback() {
    super.connectedCallback();
    this.#ensureSprings();
    this.#configureHoverSprings();
    this.#nudgeY.setConfig(NUDGE_BACK_SPRING);
    this.#nudgeRotate.setConfig(NUDGE_BACK_SPRING);
    this.#nudgeScale.setConfig(NUDGE_BACK_SPRING);
    this.#measureBloom();
    this.#applyStaticState();
    this.#syncUi();
    this.dataset.ready = '';
    this.#bindEditor();
    this.#bindReducedMotion();
    this.#bindResize();
    this.#bindReveal();
    this.#bindAway();
    document.addEventListener('visibilitychange', this.#onVisibilityChange);
    scrollContainerMediaQuery.addEventListener('change', this.#onBreakpointChange);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#ensureSprings();
    this.#configureHoverSprings();
    this.#measureBloom();
    if (!this.#hasRevealed && !prefersReducedMotion() && !window.Shopify?.designMode) {
      this.#observeReveal();
      this.#checkReveal();
    }
    if (prefersReducedMotion()) {
      this.#applyStaticState();
      return;
    }
    this.#paint();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#teardown();
  }

  /**
   * @param {Event} [event]
   */
  open = (event) => {
    event?.stopPropagation();
    this.#setOpen(true);
  };

  /**
   * @param {Event} [_event]
   */
  close = (_event) => {
    this.#setOpen(false);
  };

  /**
   * @param {Event} event
   */
  onPointerEnter = (event) => {
    if (event instanceof PointerEvent) {
      this.#pointerX = event.clientX;
      this.#pointerY = event.clientY;
      this.#pointerPending = true;
    }

    const hover = this.#canHoverMotion(event);
    if (hover) {
      this.#hovering = true;
      this.#captureRestRect();
    }

    this.#setFillOrigin(event);
    if (!hover) return;

    this.#scaleSpring.target = this.#pressed ? this.#pressScale : this.#hoverScale;
    this.#setFill(true);
    this.#restIdleHint();
    this.#bindPointerMove();
    this.#startLoop();
  };

  /**
   * @param {Event} event
   */
  onPointerLeave = (event) => {
    if ((this.#layoutDirty || this.#needsMeasure) && this.#hovering) {
      if (this.#needsMeasure) {
        this.#needsMeasure = false;
        this.#measureBloom();
      }
      this.#captureRestRect();
      this.#layoutDirty = false;
    }
    this.#setFillOrigin(event);
    this.#hovering = false;
    this.#pointerPending = false;
    this.#layoutDirty = false;
    this.#layoutRect = null;
    this.#unbindPointerMove();
    this.#magnetX.target = 0;
    this.#magnetY.target = 0;
    this.#scaleSpring.target = this.#pressed ? this.#pressScale : 1;
    this.#setFill(false);
    this.#startLoop();
  };

  onFocus = () => {
    if (this.#isOpen || prefersReducedMotion()) return;
    if (!this.refs.trigger.matches(':focus-visible')) return;

    this.#lockFillFrame();
    this.refs.trigger.style.setProperty('--skrey-fill-x', '50%');
    this.refs.trigger.style.setProperty('--skrey-fill-y', '50%');
    this.#setFill(true);
    this.#scaleSpring.target = this.#hoverScale;
    this.#restIdleHint();
    this.#startLoop();
  };

  onBlur = () => {
    if (this.#hovering) return;
    this.#setFill(false);
    this.#scaleSpring.target = this.#pressed ? this.#pressScale : 1;
    this.#startLoop();
  };

  /**
   * @param {Event} event
   */
  onPointerDown = (event) => {
    if (!(event instanceof PointerEvent)) return;

    this.#pressed = true;
    this.#scaleSpring.target = this.#pressScale;
    this.#restIdleHint();
    window.removeEventListener('pointerup', this.#onPointerUp);
    window.removeEventListener('pointercancel', this.#onPointerUp);
    window.addEventListener('pointerup', this.#onPointerUp);
    window.addEventListener('pointercancel', this.#onPointerUp);
    this.#startLoop();
  };

  /**
   * @returns {HTMLElement[]}
   */
  get #stripes() {
    const { stripes } = this.refs;
    if (Array.isArray(stripes)) {
      return stripes.filter((node) => node instanceof HTMLElement);
    }
    return stripes instanceof HTMLElement ? [stripes] : [];
  }

  #configureHoverSprings() {
    if (this.#isCaption) {
      this.#magnetX.setConfig(BOTTLE_MAGNET_SPRING);
      this.#magnetY.setConfig(BOTTLE_MAGNET_SPRING);
      this.#scaleSpring.setConfig(BOTTLE_SCALE_SPRING);
      return;
    }

    this.#magnetX.setConfig(BUTTON_MAGNET_SPRING);
    this.#magnetY.setConfig(BUTTON_MAGNET_SPRING);
    this.#scaleSpring.setConfig(BUTTON_SCALE_SPRING);
  }

  /**
   * @param {boolean} next
   */
  #setOpen(next) {
    if (this.#isOpen === next) return;

    this.#isOpen = next;
    this.#clearTimeouts();
    this.#ensureSprings();
    this.#completeReveal();
    if (this.#isBloom) {
      this.#revealSpring.target = 0;
      if (this.#revealSpring.value > 0.5) {
        this.#revealSpring.value = 0;
        this.#revealSpring.velocity = 0;
      }
    }
    if (next) {
      this.#hasOpened = true;
      this.#pendingFirstHint = false;
      this.#stopIdleHint();
      this.#releaseHover();
    }
    this.#measureBloom();
    this.#syncUi();

    if (prefersReducedMotion()) {
      this.#stopLoop();
      this.#snapSprings();
      this.#applyStaticState();
      return;
    }

    if (this.#isBloom) {
      this.#bloomSpring.setConfig(springFromDuration(next ? BLOOM_DURATION_OPEN : BLOOM_DURATION_CLOSE));
      this.#bloomSpring.target = next ? 1 : 0;
      if (!this.#isCaption) {
        this.#buttonSpring.setConfig(springFromDuration(next ? BUTTON_DURATION_OPEN : BLOOM_BUTTON_DURATION_CLOSE));
        this.#buttonSpring.target = next ? 1 : 0;
      }
      this.#startLoop();
      return;
    }

    this.#setOpenSlide(next);
  }

  /**
   * @param {boolean} next
   */
  #setOpenSlide(next) {
    const stripeConfig = springFromDuration(next ? STRIPE_DURATION_OPEN : STRIPE_DURATION_CLOSE);
    const bottleConfig = springFromDuration(next ? BOTTLE_DURATION_OPEN : BOTTLE_DURATION_CLOSE);
    const buttonConfig = springFromDuration(next ? BUTTON_DURATION_OPEN : BUTTON_DURATION_CLOSE);
    const count = Math.max(this.#stripes.length, 1);
    const span = next ? STAGGER_OPEN_SPAN : STAGGER_CLOSE_SPAN;
    const stagger = count > 1 ? span / (count - 1) : 0;
    const target = next ? 1 : 0;

    this.#bottleSpring.setConfig(bottleConfig);
    this.#buttonSpring.setConfig(buttonConfig);

    if (next) {
      this.#bottleSpring.target = target;
      if (!this.#isCaption) this.#buttonSpring.target = target;
    } else {
      const bottleTimeoutId = window.setTimeout(() => {
        this.#timeouts = this.#timeouts.filter((id) => id !== bottleTimeoutId);
        this.#bottleSpring.target = target;
        this.#startLoop();
      }, BOTTLE_DELAY_CLOSE);
      this.#timeouts.push(bottleTimeoutId);
      if (!this.#isCaption) {
        const buttonTimeoutId = window.setTimeout(() => {
          this.#timeouts = this.#timeouts.filter((id) => id !== buttonTimeoutId);
          this.#buttonSpring.target = target;
          this.#startLoop();
        }, BUTTON_DELAY_CLOSE);
        this.#timeouts.push(buttonTimeoutId);
      }
    }

    this.#stripeSprings.forEach((spring, index) => {
      spring.setConfig(stripeConfig);
      if (index === 0) {
        spring.target = target;
        return;
      }
      const timeoutId = window.setTimeout(() => {
        this.#timeouts = this.#timeouts.filter((id) => id !== timeoutId);
        spring.target = target;
        this.#startLoop();
      }, Math.round(index * stagger));
      this.#timeouts.push(timeoutId);
    });

    this.#startLoop();
  }

  #ensureSprings() {
    const count = this.#stripes.length;
    const seed = this.#isOpen ? 1 : 0;

    while (this.#stripeSprings.length < count) {
      const spring = new SpringValue(seed);
      spring.setConfig(springFromDuration(this.#isOpen ? STRIPE_DURATION_OPEN : STRIPE_DURATION_CLOSE));
      this.#stripeSprings.push(spring);
    }

    if (this.#stripeSprings.length > count) {
      this.#stripeSprings.length = count;
    }

    this.#syncStripeNodes();
  }

  #syncStripeNodes() {
    const stripes = this.#stripes;
    const same =
      this.#stripeNodes.length === stripes.length &&
      stripes.every((node, index) => node === this.#stripeNodes[index]);
    if (same) return;

    this.#stripeNodes = stripes;
    this.#stripeMedias = stripes.map((stripe) => this.#stripeMedia(stripe));
    stripes.forEach((stripe, index) => {
      stripe.style.setProperty('--skrey-stripe-index', String(index));
    });
    this.#stripesParked = false;
  }

  #snapSprings() {
    const target = this.#isOpen ? 1 : 0;
    const reveal = this.#hasRevealed ? 0 : 1;
    this.#bottleSpring.value = this.#isBloom ? 0 : target;
    this.#bottleSpring.velocity = 0;
    this.#bottleSpring.target = this.#bottleSpring.value;
    this.#buttonSpring.value = this.#isCaption ? 0 : target;
    this.#buttonSpring.velocity = 0;
    this.#buttonSpring.target = this.#buttonSpring.value;
    this.#bloomSpring.value = this.#isBloom ? target : 0;
    this.#bloomSpring.velocity = 0;
    this.#bloomSpring.target = this.#bloomSpring.value;
    this.#revealSpring.value = reveal;
    this.#revealSpring.velocity = 0;
    this.#revealSpring.target = reveal;
    this.#magnetX.value = 0;
    this.#magnetX.velocity = 0;
    this.#magnetX.target = 0;
    this.#magnetY.value = 0;
    this.#magnetY.velocity = 0;
    this.#magnetY.target = 0;
    this.#scaleSpring.value = 1;
    this.#scaleSpring.velocity = 0;
    this.#scaleSpring.target = 1;
    this.#nudgeY.value = 0;
    this.#nudgeY.velocity = 0;
    this.#nudgeY.target = 0;
    this.#nudgeRotate.value = 0;
    this.#nudgeRotate.velocity = 0;
    this.#nudgeRotate.target = 0;
    this.#nudgeScale.value = 1;
    this.#nudgeScale.velocity = 0;
    this.#nudgeScale.target = 1;
    this.#nudgePhase = 'rest';
    this.#stripeSprings.forEach((spring) => {
      spring.value = target;
      spring.velocity = 0;
      spring.target = target;
    });
  }

  #applyStaticState() {
    this.#styles.forget();
    this.#stripesParked = false;
    const idle = this.refs.idle;
    const trigger = this.refs.trigger;
    const visual = this.refs.visual;
    const caption = this.refs.caption;
    const reveal = this.refs.reveal;
    const reduced = prefersReducedMotion();

    idle.style.transform = '';
    idle.style.opacity = '';
    idle.style.filter = '';
    trigger.style.opacity = '';
    trigger.style.removeProperty('--skrey-fill-x');
    trigger.style.removeProperty('--skrey-fill-y');
    visual.style.transform = '';
    visual.style.opacity = '';
    visual.style.filter = '';
    this.refs.nudge.style.transform = '';
    if (caption) {
      caption.style.opacity = '';
      caption.style.filter = '';
    }
    if (reveal) {
      reveal.style.clipPath = '';
      reveal.style.transition = '';
    }
    this.refs.panel.style.clipPath = '';
    this.refs.panel.style.maskImage = '';
    this.refs.panel.style.webkitMaskImage = '';
    this.refs.panel.style.opacity = '';

    this.#stripes.forEach((stripe) => {
      const media = this.#stripeMedia(stripe);
      stripe.style.transform = '';
      stripe.style.opacity = '';
      stripe.style.filter = '';
      stripe.toggleAttribute('data-settled', reduced && this.#isOpen);
      if (media !== stripe) {
        media.style.filter = '';
        media.style.transform = '';
      }
    });

    if (reduced) return;

    this.#paint();
  }

  /**
   * @param {{ scene?: boolean, magnet?: boolean, nudge?: boolean }} [parts]
   */
  #paint(parts = { scene: true, nudge: true }) {
    if (parts.scene) {
      if (this.#isBloom) this.#paintBloom();
      else this.#paintSlide();
    } else if (parts.magnet) {
      this.#paintHoverTransform();
    }

    if (parts.nudge) this.#paintNudge();
  }

  #paintHoverTransform() {
    this.#write(this.refs.visual, 'transform', this.#hoverTransform());
  }

  #hoverTransform() {
    const x = px(this.#magnetX.value);
    if (this.#isBloom && this.#isCaption) {
      const depth = smoothstep(this.#bloomSpring.value);
      const y = px(this.#magnetY.value + depth * BLOOM_BOTTLE_SINK);
      const scale = milli((1 - (1 - BLOOM_BOTTLE_SCALE) * depth) * this.#scaleSpring.value);
      return `translate3d(${x}px, ${y}px, 0) scale(${scale})`;
    }

    const y = px(this.#magnetY.value);
    return `translate3d(${x}px, ${y}px, 0) scale(${milli(this.#scaleSpring.value)})`;
  }

  #nudgeTransform() {
    return `translate3d(0px, ${px(this.#nudgeY.value)}px, 0) rotate(${px(this.#nudgeRotate.value)}deg) scale(${milli(this.#nudgeScale.value)})`;
  }

  #paintSlide() {
    this.#syncStripeNodes();
    const bottleProgress = this.#bottleSpring.value;
    const buttonProgress = this.#buttonSpring.value;
    const idle = this.refs.idle;
    const trigger = this.refs.trigger;
    const visual = this.refs.visual;
    const caption = this.refs.caption;
    const scale = milli(1 - (1 - BOTTLE_SCALE_HIDDEN) * bottleProgress);
    const bottleShown = 1 - bottleProgress > 0.004;
    const buttonShown = 1 - buttonProgress > 0.004;

    this.#write(idle, 'transform', `scale(${scale})`);
    this.#write(idle, 'opacity', alpha(1 - bottleProgress));
    this.#write(idle, 'filter', blurFilter(bottleProgress * BOTTLE_BLUR_MAX, bottleShown));

    if (this.#isCaption) {
      this.#write(trigger, 'opacity', '');
      this.#write(visual, 'transform', this.#hoverTransform());
      this.#write(visual, 'filter', '');
      this.#write(visual, 'opacity', '');
      if (caption) {
        this.#write(caption, 'opacity', '');
        this.#write(caption, 'filter', '');
      }
    } else {
      this.#write(trigger, 'opacity', alpha(1 - buttonProgress));
      this.#write(visual, 'transform', this.#hoverTransform());
      this.#write(visual, 'filter', blurFilter(buttonProgress * BOTTLE_BLUR_MAX, buttonShown));
    }

    this.#write(this.refs.panel, 'clipPath', '');
    this.#write(this.refs.panel, 'maskImage', '');
    this.#write(this.refs.panel, 'webkitMaskImage', '');

    this.#stripeNodes.forEach((stripe, index) => {
      const progress = this.#stripeSprings[index]?.value ?? 0;
      const from = index % 2 === 0 ? 100 : -100;
      const media = this.#stripeMedias[index] ?? stripe;
      const shown = progress > 0.001;
      this.#write(stripe, 'transform', `translate3d(${milli(from * (1 - progress))}%, 0, 0)`);
      this.#write(stripe, 'filter', '');
      if (media !== stripe) this.#write(media, 'transform', '');
      this.#write(media, 'filter', blurFilter((1 - progress) * STRIPE_BLUR_MAX, shown));
      this.#setStripeSettled(stripe, this.#isOpen ? progress >= STRIPE_COPY_REVEAL : progress >= STRIPE_SETTLED);
    });
  }

  #paintBloom() {
    this.#syncStripeNodes();
    const reveal = this.#revealSpring.value;
    const bloom = this.#bloomSpring.value;
    const shown = 1 - reveal;
    const depth = smoothstep(bloom);
    const presence = shown * (1 - depth);
    const scale = 1 - (1 - BLOOM_BOTTLE_SCALE) * depth;
    const sink = depth * BLOOM_BOTTLE_SINK;
    const bottleBlur = depth * BOTTLE_BLUR_MAX;
    const visual = this.refs.visual;
    const caption = this.refs.caption;
    const idle = this.refs.idle;
    const trigger = this.refs.trigger;
    const bottleShown = presence > 0.004;

    if (this.#isCaption) {
      this.#write(idle, 'transform', '');
      this.#write(idle, 'opacity', '');
      this.#write(idle, 'filter', '');
      this.#write(trigger, 'opacity', '');
      this.#write(visual, 'transform', this.#hoverTransform());
      this.#write(visual, 'opacity', alpha(presence));
      this.#write(visual, 'filter', blurFilter(bottleBlur, bottleShown));
      if (caption) {
        this.#write(caption, 'opacity', alpha(presence));
        this.#write(caption, 'filter', blurFilter(bottleBlur * 0.6, bottleShown));
      }
    } else {
      const buttonProgress = this.#buttonSpring.value;
      const buttonShown = 1 - buttonProgress > 0.004;
      this.#write(idle, 'transform', `translate3d(0px, ${px(sink)}px, 0) scale(${milli(scale)})`);
      this.#write(idle, 'opacity', alpha(presence));
      this.#write(idle, 'filter', blurFilter(bottleBlur, bottleShown));
      this.#write(trigger, 'opacity', alpha(1 - buttonProgress));
      this.#write(visual, 'transform', this.#hoverTransform());
      this.#write(visual, 'filter', blurFilter(buttonProgress * BOTTLE_BLUR_MAX, buttonShown));
      this.#write(visual, 'opacity', '');
    }

    const radius = bloom * (this.#coverRadius + BLOOM_FEATHER);
    this.#write(this.refs.panel, 'clipPath', '');

    if (radius < 1) {
      this.#write(this.refs.panel, 'maskImage', 'linear-gradient(transparent, transparent)');
      this.#write(this.refs.panel, 'webkitMaskImage', 'linear-gradient(transparent, transparent)');
      this.#parkBloomStripes();
      this.#stripeNodes.forEach((stripe) => this.#setStripeSettled(stripe, false));
      return;
    }

    this.#stripesParked = false;
    const radiusPx = Math.round(radius);
    const solid = Math.max(0, radiusPx - BLOOM_FEATHER);
    const mask = `radial-gradient(circle at ${px(this.#originX)}px ${px(this.#originY)}px, #000 ${solid}px, transparent ${radiusPx}px)`;
    this.#write(this.refs.panel, 'maskImage', mask);
    this.#write(this.refs.panel, 'webkitMaskImage', mask);

    const stripeSettled = this.#isOpen && bloom >= STRIPE_SETTLED;
    const stripeScale = milli(STRIPE_SCALE - (STRIPE_SCALE - 1) * bloom);
    const stripeBlur = blurFilter((1 - bloom) * STRIPE_BLUR_MAX, true);
    this.#stripeNodes.forEach((stripe, index) => {
      const media = this.#stripeMedias[index] ?? stripe;
      this.#write(stripe, 'transform', '');
      if (media !== stripe) this.#write(stripe, 'filter', '');
      this.#write(media, 'transform', `scale(${stripeScale})`);
      this.#write(media, 'filter', stripeBlur);
      this.#setStripeSettled(stripe, stripeSettled);
    });
  }

  #parkBloomStripes() {
    if (this.#stripesParked) return;

    this.#stripeNodes.forEach((stripe, index) => {
      const media = this.#stripeMedias[index] ?? stripe;
      this.#write(stripe, 'transform', '');
      this.#write(stripe, 'filter', '');
      this.#write(media, 'transform', '');
      this.#write(media, 'filter', '');
      this.#setStripeSettled(stripe, false);
    });
    this.#stripesParked = true;
  }

  #paintNudge() {
    const nudge = this.refs.nudge;
    if (!nudge) return;
    this.#write(nudge, 'transform', this.#nudgeTransform());
  }

  /**
   * @param {HTMLElement | undefined} node
   * @param {string} property
   * @param {string} value
   */
  #write(node, property, value) {
    this.#styles.set(node, property, value);
  }

  /**
   * @param {HTMLElement} stripe
   * @returns {HTMLElement}
   */
  #stripeMedia(stripe) {
    return stripe.querySelector('.skrey-smell-this-stripe__zoom') ?? stripe;
  }

  /**
   * @param {HTMLElement} stripe
   * @param {boolean} settled
   */
  #setStripeSettled(stripe, settled) {
    if (settled) stripe.setAttribute('data-settled', '');
    else stripe.removeAttribute('data-settled');
  }

  #measureBloom() {
    if (!this.#isBloom) return;

    const host = this.getBoundingClientRect();
    const bottle = this.#bloomOriginEl().getBoundingClientRect();
    const width = this.clientWidth;
    const height = this.clientHeight;

    this.#originX = bottle.left - host.left + bottle.width / 2;
    this.#originY = bottle.top - host.top + bottle.height * BLOOM_ORIGIN_Y;

    const corners = [
      [0, 0],
      [width, 0],
      [0, height],
      [width, height],
    ];
    this.#coverRadius = Math.max(
      1,
      ...corners.map(([x, y]) => Math.hypot(x - this.#originX, y - this.#originY)),
    ) * 1.04;
  }

  #bloomOriginEl() {
    if (this.#isCaption) return this.refs.visual;
    return this.refs.nudge ?? this.refs.idle;
  }

  #startLoop() {
    if (this.#rafId !== null) return;
    this.#lastTime = performance.now();
    this.#rafId = requestAnimationFrame(this.#tick);
  }

  #stopLoop() {
    if (this.#rafId === null) return;
    cancelAnimationFrame(this.#rafId);
    this.#rafId = null;
  }

  /**
   * @param {number} now
   */
  #tick = (now) => {
    if (this.#needsMeasure) {
      this.#needsMeasure = false;
      this.#measureBloom();
      if (this.#hovering) this.#layoutDirty = true;
    }

    if (this.#layoutDirty && this.#hovering) {
      this.#layoutDirty = false;
      this.#captureRestRect();
      this.#pointerPending = true;
    }

    if (this.#pointerPending && this.#hovering) {
      this.#pointerPending = false;
      this.#syncRestCenter();
      this.#updateMagnetFromPointer();
    }

    const dt = (now - this.#lastTime) / 1000;
    this.#lastTime = now;

    const bottleMoved = stepSpring(this.#bottleSpring, dt);
    const buttonMoved = stepSpring(this.#buttonSpring, dt);
    const bloomMoved = stepSpring(this.#bloomSpring, dt);
    const revealMoved = stepSpring(this.#revealSpring, dt);
    let stripesMoved = false;
    for (const spring of this.#stripeSprings) {
      if (stepSpring(spring, dt)) stripesMoved = true;
    }
    const magnetXMoved = stepSpring(this.#magnetX, dt);
    const magnetYMoved = stepSpring(this.#magnetY, dt);
    const scaleMoved = stepSpring(this.#scaleSpring, dt);
    const nudgeYMoved = stepSpring(this.#nudgeY, dt);
    const nudgeRotateMoved = stepSpring(this.#nudgeRotate, dt);
    const nudgeScaleMoved = stepSpring(this.#nudgeScale, dt);
    const magnetMoved = magnetXMoved || magnetYMoved || scaleMoved;
    const nudgeMoved = nudgeYMoved || nudgeRotateMoved || nudgeScaleMoved;

    this.#finishIdleHintPhase();
    this.#armFirstIdleHintIfReady();

    const sceneMoved = bottleMoved || buttonMoved || bloomMoved || revealMoved || stripesMoved;
    if (sceneMoved) this.#paint({ scene: true, nudge: nudgeMoved });
    else if (magnetMoved) this.#paint({ scene: false, magnet: true, nudge: nudgeMoved });
    else if (nudgeMoved) this.#paint({ scene: false, nudge: true });

    if (
      this.#bottleSpring.settled &&
      this.#buttonSpring.settled &&
      this.#bloomSpring.settled &&
      this.#revealSpring.settled &&
      this.#magnetX.settled &&
      this.#magnetY.settled &&
      this.#scaleSpring.settled &&
      this.#nudgeY.settled &&
      this.#nudgeRotate.settled &&
      this.#nudgeScale.settled &&
      this.#stripeSprings.every((spring) => spring.settled)
    ) {
      this.#rafId = null;
      return;
    }

    this.#rafId = requestAnimationFrame(this.#tick);
  };

  #syncUi() {
    this.dataset.state = this.#isOpen ? 'open' : 'idle';
    this.refs.trigger.setAttribute('aria-expanded', this.#isOpen ? 'true' : 'false');
    this.refs.trigger.setAttribute('aria-hidden', this.#isOpen ? 'true' : 'false');
    this.refs.trigger.tabIndex = this.#isOpen ? -1 : 0;
    document.removeEventListener('keydown', this.#onKeydown);

    if (this.#isOpen) {
      this.refs.panel.removeAttribute('inert');
      document.addEventListener('keydown', this.#onKeydown);
    } else {
      this.refs.panel.setAttribute('inert', '');
    }
  }

  /**
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key === 'Escape') this.#setOpen(false);
  };

  #bindAway() {
    this.#unbindAway();
    if (window.Shopify?.designMode) return;
    this.#awayScrollTarget = getScrollEventTarget();
    this.#awayScrollTarget.addEventListener('scroll', this.#onAwayScroll, { passive: true });
  }

  #unbindAway() {
    this.#awayScrollTarget?.removeEventListener('scroll', this.#onAwayScroll);
    this.#awayScrollTarget = null;
  }

  #onAwayScroll = () => {
    if (!this.#isOpen || window.Shopify?.designMode) return;
    this.#scheduleScrollCheck();
  };

  #onRevealScroll = () => {
    this.#scheduleScrollCheck();
  };

  #scheduleScrollCheck() {
    if (this.#scrollRaf !== null) return;
    this.#scrollRaf = requestAnimationFrame(() => {
      this.#scrollRaf = null;
      if (!this.isConnected) return;
      if (!this.#hasRevealed) this.#checkReveal();
      if (this.#isOpen && !window.Shopify?.designMode && this.#hasScrolledAway()) this.close();
    });
  };

  #hasScrolledAway() {
    const root = getIntersectionRoot();
    const view = root ? root.getBoundingClientRect() : new DOMRect(0, 0, window.innerWidth, window.innerHeight);
    const rect = (this.closest('.shopify-section') ?? this).getBoundingClientRect();
    if (rect.height < 8 || view.height < 8) return false;

    const visibleTop = Math.max(view.top, rect.top);
    const visibleBottom = Math.min(view.bottom, rect.bottom);
    const visible = Math.max(0, visibleBottom - visibleTop);
    const cover = Math.min(rect.height, view.height);
    return visible < cover * AWAY_COVER;
  }

  /**
   * @param {Event} event
   */
  #setFillOrigin(event) {
    if (this.#isCaption || !this.refs.reveal) return;
    if (!(event instanceof PointerEvent)) return;
    if (event.pointerType === 'touch') return;
    if (!this.#hoverMedia.matches) return;

    this.#lockFillFrame();
    if (!this.#hovering || !this.#layoutRect) this.#captureRestRect();
    const rect = this.#layoutRect;
    if (!rect || rect.width === 0 || rect.height === 0) return;

    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    this.refs.trigger.style.setProperty('--skrey-fill-x', `${clamp(x, 0, 100)}%`);
    this.refs.trigger.style.setProperty('--skrey-fill-y', `${clamp(y, 0, 100)}%`);
  }

  #lockFillFrame() {
    const reveal = this.refs.reveal;
    if (!reveal) return;
    const current = getComputedStyle(reveal).clipPath;
    if (current && current !== 'none') reveal.style.clipPath = current;
  }

  /**
   * @param {Event} [event]
   * @returns {boolean}
   */
  #canHoverMotion(event) {
    if (this.#isOpen || prefersReducedMotion()) return false;
    if (event instanceof PointerEvent && event.pointerType === 'touch') return false;
    return this.#hoverMedia.matches;
  }

  #captureRestRect() {
    const node = this.#isCaption ? this.refs.visual : this.refs.trigger;
    const rect = node.getBoundingClientRect();

    if (!this.#isCaption) {
      this.#layoutRect = {
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
      };
    } else {
      const scale = this.#scaleSpring.value || 1;
      const width = rect.width / scale;
      const height = rect.height / scale;
      this.#layoutRect = {
        left: rect.left - this.#magnetX.value - (width * (1 - scale)) / 2,
        top: rect.top - this.#magnetY.value - height * 0.3 * (1 - scale),
        width,
        height,
      };
    }

    this.#syncRestCenter();
  }

  /**
   * Caption measures the transformed visual. Rebuild that center from the
   * cached layout box plus the springs we already applied, so pointermove
   * does not call getBoundingClientRect.
   */
  #syncRestCenter() {
    const rect = this.#layoutRect;
    if (!rect) {
      this.#restRect = null;
      return;
    }

    if (!this.#isCaption) {
      this.#restRect = {
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
        cx: rect.left + rect.width / 2,
        cy: rect.top + rect.height / 2,
      };
      return;
    }

    const scale = this.#scaleSpring.value || 1;
    const dx = this.#magnetX.value;
    const dy = this.#magnetY.value + 0.2 * (scale - 1) * rect.height;
    const width = rect.width * scale;
    const height = rect.height * scale;
    const cx = rect.left + rect.width / 2 + dx;
    const cy = rect.top + rect.height / 2 + dy;
    this.#restRect = {
      left: cx - width / 2,
      top: cy - height / 2,
      width,
      height,
      cx,
      cy,
    };
  }

  /**
   * @param {Event} event
   */
  #updateMagnetFromPointer() {
    if (!this.#restRect) return;

    const pull = this.#isCaption ? BOTTLE_MAGNET_PULL : BUTTON_MAGNET_PULL;
    const max = this.#isCaption ? BOTTLE_MAGNET_MAX : BUTTON_MAGNET_MAX;
    const dx = this.#pointerX - this.#restRect.cx;
    const dy = this.#pointerY - this.#restRect.cy;
    this.#magnetX.target = clamp(dx * pull, -max, max);
    this.#magnetY.target = clamp(dy * pull, -max, max);
  }

  #bindPointerMove() {
    this.#unbindPointerMove();
    document.addEventListener('pointermove', this.#onPointerMove, { passive: true });
    this.#hoverScrollTarget = getScrollEventTarget();
    this.#hoverScrollTarget.addEventListener('scroll', this.#onHoverScroll, { passive: true });
  }

  #unbindPointerMove() {
    document.removeEventListener('pointermove', this.#onPointerMove);
    this.#hoverScrollTarget?.removeEventListener('scroll', this.#onHoverScroll);
    this.#hoverScrollTarget = null;
  }

  /**
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    if (!this.#hovering || !this.#canHoverMotion(event)) return;
    this.#pointerX = event.clientX;
    this.#pointerY = event.clientY;
    this.#pointerPending = true;
    this.#startLoop();
  };

  #onHoverScroll = () => {
    if (!this.#hovering) return;
    this.#layoutDirty = true;
    this.#startLoop();
  };

  #onPointerUp = () => {
    this.#pressed = false;
    window.removeEventListener('pointerup', this.#onPointerUp);
    window.removeEventListener('pointercancel', this.#onPointerUp);
    this.#scaleSpring.target = this.#hovering && !this.#isOpen && !prefersReducedMotion()
      ? this.#hoverScale
      : 1;
    this.#startLoop();
  };

  #releaseHover() {
    this.#hovering = false;
    this.#pressed = false;
    this.#pointerPending = false;
    this.#layoutDirty = false;
    this.#layoutRect = null;
    this.#unbindPointerMove();
    window.removeEventListener('pointerup', this.#onPointerUp);
    window.removeEventListener('pointercancel', this.#onPointerUp);
    this.#magnetX.target = 0;
    this.#magnetY.target = 0;
    this.#scaleSpring.target = 1;
    this.#setFill(false, true);
  }

  /**
   * @param {boolean} filled
   * @param {boolean} [instant]
   */
  #setFill(filled, instant = false) {
    const reveal = this.refs.reveal;
    if (!reveal) return;

    const x = this.refs.trigger.style.getPropertyValue('--skrey-fill-x') || '50%';
    const y = this.refs.trigger.style.getPropertyValue('--skrey-fill-y') || '50%';
    const next = `circle(${filled ? FILL_RADIUS : 0}% at ${x} ${y})`;

    if (instant || prefersReducedMotion()) {
      reveal.style.transition = 'none';
      reveal.style.clipPath = next;
      return;
    }

    reveal.style.transition = `clip-path ${filled ? FILL_MS_OPEN : FILL_MS_CLOSE}ms ${FILL_EASE}`;
    reveal.style.clipPath = next;
  }

  #clearTimeouts() {
    this.#timeouts.forEach((id) => window.clearTimeout(id));
    this.#timeouts = [];
  }

  #bindReducedMotion() {
    this.#reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#reducedMotionQuery.addEventListener('change', this.#onReducedMotionChange);
  }

  #onReducedMotionChange = () => {
    this.#clearTimeouts();
    this.#releaseHover();
    if (prefersReducedMotion()) {
      this.#pendingFirstHint = false;
      this.#completeReveal();
      this.#stopIdleHint();
      this.#stopLoop();
      this.#snapSprings();
    } else if (this.#nudgeEnabled) {
      this.#armIdleHint(IDLE_HINT_FIRST);
    }
    this.#applyStaticState();
    this.#syncUi();
  };

  #bindResize() {
    this.#resizeObserver = new ResizeObserver(() => {
      this.#needsMeasure = true;
      if (this.#rafId === null) this.#scheduleMeasure();
    });
    this.#resizeObserver.observe(this);
    this.#resizeObserver.observe(this.#bloomOriginEl());

    const image = this.refs.idle.querySelector('img');
    image?.addEventListener('load', this.#onMediaLoad);
  }

  #scheduleMeasure() {
    if (this.#measureRaf !== null) return;
    this.#measureRaf = requestAnimationFrame(() => {
      this.#measureRaf = null;
      if (!this.isConnected || !this.#needsMeasure) return;
      this.#needsMeasure = false;
      this.#measureBloom();
      if (this.#hovering) {
        this.#layoutDirty = true;
        this.#startLoop();
        return;
      }
      if (!prefersReducedMotion()) this.#paint();
    });
  }

  #onMediaLoad = () => {
    this.#measureBloom();
    if (this.#hovering) {
      this.#layoutDirty = true;
      this.#startLoop();
    }
    if (!this.#hasRevealed) this.#checkReveal();
    if (!prefersReducedMotion() && this.#rafId === null) this.#paint();
  };

  #bindReveal() {
    if (prefersReducedMotion() || window.Shopify?.designMode) {
      this.#completeReveal();
      this.#revealSpring.value = 0;
      this.#revealSpring.target = 0;
      this.#bottleSpring.value = 0;
      this.#bottleSpring.target = 0;
      this.#buttonSpring.value = 0;
      this.#buttonSpring.target = 0;
      this.#applyStaticState();
      return;
    }

    if (this.#isBloom) {
      this.#revealSpring.value = 1;
      this.#revealSpring.velocity = 0;
      this.#revealSpring.target = 1;
    } else {
      this.#bottleSpring.value = 1;
      this.#bottleSpring.velocity = 0;
      this.#bottleSpring.target = 1;
    }
    if (!this.#isCaption) {
      this.#buttonSpring.value = 1;
      this.#buttonSpring.velocity = 0;
      this.#buttonSpring.target = 1;
    }
    this.#paint();

    this.#observeReveal();
    this.#bindRevealScroll();
    this.#checkReveal();
  }

  #observeReveal() {
    this.#revealObserver?.disconnect();
    this.#revealObserver = new IntersectionObserver(this.#onRevealIntersect, {
      root: getIntersectionRoot(),
      threshold: [0, 0.15, 0.35],
      rootMargin: '0px 0px -12% 0px',
    });
    this.#revealObserver.observe(this.#bottleTarget());
  }

  #bindRevealScroll() {
    this.#unbindRevealScroll();
    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#onRevealScroll, { passive: true });
  }

  #unbindRevealScroll() {
    this.#scrollTarget?.removeEventListener('scroll', this.#onRevealScroll);
    this.#scrollTarget = null;
  }

  #onBreakpointChange = () => {
    this.#bindAway();
    if (this.#hasRevealed || prefersReducedMotion()) return;
    this.#observeReveal();
    this.#bindRevealScroll();
    this.#checkReveal();
  };

  #bottleTarget() {
    return this.refs.idle.querySelector('.skrey-smell-this__idle-image') ?? this.refs.idle;
  }

  #checkReveal() {
    if (this.#hasRevealed || this.#isOpen || prefersReducedMotion()) return;
    if (!this.#isBottleInView()) return;
    this.#playReveal();
  }

  #isBottleInView() {
    const bottle = this.#bottleTarget();
    const root = getIntersectionRoot();
    const rootRect = root ? root.getBoundingClientRect() : new DOMRect(0, 0, window.innerWidth, window.innerHeight);
    const bottleRect = bottle.getBoundingClientRect();
    if (bottleRect.height < 8) return false;

    const inset = rootRect.height * 0.12;
    const visibleTop = Math.max(rootRect.top, bottleRect.top);
    const visibleBottom = Math.min(rootRect.bottom - inset, bottleRect.bottom);
    const visible = Math.max(0, visibleBottom - visibleTop);
    return visible / bottleRect.height >= 0.2;
  }

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  #onRevealIntersect = (entries) => {
    const entry = entries[entries.length - 1];
    if (!entry?.isIntersecting || this.#hasRevealed) return;
    this.#playReveal();
  };

  #playReveal() {
    if (this.#hasRevealed || this.#isOpen) return;

    this.#completeReveal();
    if (this.#nudgeEnabled) this.#pendingFirstHint = true;

    if (this.#isBloom) {
      this.#revealSpring.setConfig(springFromDuration(REVEAL_DURATION));
      this.#revealSpring.target = 0;
    } else {
      this.#bottleSpring.setConfig(springFromDuration(BOTTLE_DURATION_CLOSE));
      this.#bottleSpring.target = 0;
    }

    if (!this.#isCaption) {
      this.#buttonSpring.setConfig(springFromDuration(BUTTON_DURATION_CLOSE));
      const timeoutId = window.setTimeout(() => {
        this.#timeouts = this.#timeouts.filter((id) => id !== timeoutId);
        if (this.#isOpen) return;
        this.#buttonSpring.target = 0;
        this.#startLoop();
      }, REVEAL_BUTTON_DELAY);
      this.#timeouts.push(timeoutId);
    }

    this.#paint();
    this.#startLoop();
  }

  #completeReveal() {
    this.#hasRevealed = true;
    this.#revealObserver?.disconnect();
    this.#revealObserver = null;
    this.#unbindRevealScroll();
    this.removeAttribute('data-reveal');
  }

  #bindEditor() {
    if (!window.Shopify?.designMode) return;
    document.addEventListener('shopify:block:select', this.#onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.#onBlockDeselect);
  }

  /**
   * @param {Event} event
   */
  #onBlockSelect = (event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;
    this.#setOpen(true);
  };

  /**
   * @param {Event} event
   */
  #onBlockDeselect = (event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;
    this.#setOpen(false);
  };

  #onVisibilityChange = () => {
    if (document.hidden) this.#restIdleHint();
  };

  /**
   * @param {number} delay
   */
  #armIdleHint(delay) {
    this.#clearIdleHintTimer();
    if (!this.isConnected || !this.#nudgeEnabled || this.#hasOpened || prefersReducedMotion() || window.Shopify?.designMode) return;

    this.#idleHintTimer = window.setTimeout(this.#onIdleHintTick, delay * 1000);
  }

  #clearIdleHintTimer() {
    if (this.#idleHintTimer === null) return;
    window.clearTimeout(this.#idleHintTimer);
    this.#idleHintTimer = null;
  }

  #stopIdleHint() {
    this.#pendingFirstHint = false;
    this.#clearIdleHintTimer();
    this.#restIdleHint();
  }

  #restIdleHint() {
    if (this.#nudgePhase === 'rest' && this.#nudgeY.settled && this.#nudgeRotate.settled && this.#nudgeScale.settled) {
      return;
    }

    this.#nudgePhase = 'back';
    this.#nudgeY.setConfig(NUDGE_BACK_SPRING);
    this.#nudgeRotate.setConfig(NUDGE_BACK_SPRING);
    this.#nudgeScale.setConfig(NUDGE_BACK_SPRING);
    this.#nudgeY.target = 0;
    this.#nudgeRotate.target = 0;
    this.#nudgeScale.target = 1;
    if (this.isConnected) this.#startLoop();
  }

  #armFirstIdleHintIfReady() {
    if (!this.#pendingFirstHint || !this.#nudgeEnabled) return;
    const appearSettled = this.#isBloom
      ? this.#revealSpring.settled && this.#revealSpring.target === 0
      : this.#bottleSpring.settled && this.#bottleSpring.target === 0;
    if (!appearSettled) return;

    this.#pendingFirstHint = false;
    this.#armIdleHint(IDLE_HINT_FIRST);
  }

  #onIdleHintTick = () => {
    this.#idleHintTimer = null;
    if (!this.#nudgeEnabled || this.#hasOpened || prefersReducedMotion() || window.Shopify?.designMode) return;

    if (!this.#canPlayIdleHint()) {
      this.#armIdleHint(1.5);
      return;
    }

    this.#playIdleHint();
    this.#armIdleHint(IDLE_HINT_REPEAT);
  };

  #canPlayIdleHint() {
    if (!this.#nudgeEnabled || this.#hasOpened || this.#isOpen || this.#hovering || this.#pressed) return false;
    if (!this.#hasRevealed || prefersReducedMotion() || window.Shopify?.designMode) return false;
    if (document.hidden) return false;
    if (this.#nudgePhase !== 'rest') return false;
    if (this.#isBloom && (!this.#revealSpring.settled || this.#revealSpring.target !== 0)) return false;
    if (!this.#isBloom && (!this.#bottleSpring.settled || this.#bottleSpring.target !== 0)) return false;
    if (this.#isBloom && (!this.#bloomSpring.settled || this.#bloomSpring.value > 0.01)) return false;
    return this.#isBottleInView();
  }

  #playIdleHint() {
    this.#nudgeSign *= -1;
    this.#nudgePhase = 'out';
    this.#nudgeY.setConfig(NUDGE_OUT_SPRING);
    this.#nudgeRotate.setConfig(NUDGE_OUT_SPRING);
    this.#nudgeScale.setConfig(NUDGE_OUT_SPRING);
    this.#nudgeY.target = NUDGE_LIFT;
    this.#nudgeRotate.target = NUDGE_ROTATE * this.#nudgeSign;
    this.#nudgeScale.target = NUDGE_SCALE;
    this.#startLoop();
  }

  #finishIdleHintPhase() {
    if (this.#nudgePhase === 'out' && this.#nudgeY.settled && this.#nudgeRotate.settled && this.#nudgeScale.settled) {
      this.#nudgePhase = 'back';
      this.#nudgeY.setConfig(NUDGE_BACK_SPRING);
      this.#nudgeRotate.setConfig(NUDGE_BACK_SPRING);
      this.#nudgeScale.setConfig(NUDGE_BACK_SPRING);
      this.#nudgeY.target = 0;
      this.#nudgeRotate.target = 0;
      this.#nudgeScale.target = 1;
      return;
    }

    if (this.#nudgePhase === 'back' && this.#nudgeY.settled && this.#nudgeRotate.settled && this.#nudgeScale.settled) {
      this.#nudgePhase = 'rest';
    }
  }

  #teardown() {
    this.#releaseHover();
    this.#clearTimeouts();
    this.#stopIdleHint();
    this.#stopLoop();
    if (this.#scrollRaf !== null) cancelAnimationFrame(this.#scrollRaf);
    if (this.#measureRaf !== null) cancelAnimationFrame(this.#measureRaf);
    this.#scrollRaf = null;
    this.#measureRaf = null;
    this.#revealObserver?.disconnect();
    this.#revealObserver = null;
    this.#unbindRevealScroll();
    this.#unbindAway();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.refs.idle.querySelector('img')?.removeEventListener('load', this.#onMediaLoad);
    document.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
    this.#reducedMotionQuery?.removeEventListener('change', this.#onReducedMotionChange);
    scrollContainerMediaQuery.removeEventListener('change', this.#onBreakpointChange);
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.#onBlockDeselect);
  }
}

if (!customElements.get('skrey-smell-this')) {
  customElements.define('skrey-smell-this', SkreySmellThis);
}
